import { callGateway, callGatewayBytes, type CallerIdentity } from "./gateway";
import { ApiError } from "./errors";
import { config } from "../config";
import type { PortalLayout, PortalLogoSize } from "../portal/api";

/**
 * Portal themes, stored by radhotspotapi (v0.1.26) and reached through the
 * radaccess gateway's /radhotspot prefix. Contract: radius-wiki
 * portal-brand-from-logo.md, "API — radhotspotapi".
 *
 * 🔑 EVERY WRITE IS PUT. radhotspotapi derives the operation from the method and
 * the operator's role may update but not create, so a POST would be refused by
 * the service after the gateway had allowed it.
 *
 * 🔑 THE SAVE BODY IS EXACTLY ground, ink, accent and an optional logo and copy.
 * The server refuses unknown fields, and `mode` in particular: it is derived
 * from the ground on the server, so the console can preview it but never send it.
 * `copy` needs radhotspotapi v0.1.27 (portal text per theme); `backdrop`,
 * `background`, `layout` and `cardOpacity` need v0.1.28 (page background and
 * layout); `logoSize` needs v0.1.31. Every optional field left out keeps what
 * is stored.
 */

const PREFIX = "/radhotspot/theme";
const enc = (s: string) => encodeURIComponent(s);

/** radhotspotapi's own rule for a theme name. */
export const THEME_NAME = /^[A-Za-z0-9_-]{1,64}$/;
export const MAX_THEMES = 10;

export interface SavedTheme {
  name: string;
  ground: string;
  ink: string;
  accent: string;
  mode: "light" | "dark";
  logo_md5?: string;
  /** The theme's page text by catalogue key (src/portal/copy-catalogue.json). Absent when it sets none. */
  copy?: Record<string, string>;
  /** Page colour. Absent: the ground colour. */
  backdrop?: string;
  background_md5?: string;
  layout?: PortalLayout;
  card_opacity?: number;
  /** Absent: medium. */
  logo_size?: PortalLogoSize;
  updated_by: string;
  updated_at: string;
  published: boolean;
}

/** The theme the portal renders: the namespace's own published theme, else its nearest ancestor's. */
export interface EffectiveTheme {
  namespace: string;
  name: string;
  ground: string;
  ink: string;
  accent: string;
  mode: "light" | "dark";
  logo_md5?: string;
  copy?: Record<string, string>;
  /** Page colour. Absent: the ground colour. */
  backdrop?: string;
  background_md5?: string;
  layout?: PortalLayout;
  card_opacity?: number;
  logo_size?: PortalLogoSize;
  inherited: boolean;
}

export interface ThemeList {
  published: string | null;
  effective: EffectiveTheme | null;
  themes: SavedTheme[];
}

export interface ThemeDraft {
  ground: string;
  ink: string;
  accent: string;
  /** Base64 PNG or JPEG, at most 150 KB decoded. Absent keeps the stored logo. */
  logo?: string;
  /** The page text overrides. Absent keeps the stored text; {} clears it. */
  copy?: Record<string, string>;
  /** #RRGGBB, or "" for the ground colour. Absent keeps the stored one. */
  backdrop?: string;
  /** Base64 PNG or JPEG, at most 600 KB decoded; "" removes it. Absent keeps the stored image. */
  background?: string;
  layout?: PortalLayout;
  /** 80-100. Absent keeps the stored value. */
  cardOpacity?: number;
  /** Absent keeps the stored size. */
  logoSize?: PortalLogoSize;
}

export const listThemes = (i: CallerIdentity, ns: string) =>
  callGateway<ThemeList>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}`);

export function saveTheme(i: CallerIdentity, ns: string, name: string, d: ThemeDraft) {
  if (!THEME_NAME.test(name)) {
    return Promise.reject(new ApiError("bad-request", "A theme name is 1-64 letters, digits, '-' or '_'."));
  }
  const body: ThemeDraft = { ground: d.ground, ink: d.ink, accent: d.accent };
  if (d.logo !== undefined) body.logo = d.logo;
  if (d.copy !== undefined) body.copy = d.copy;
  if (d.backdrop !== undefined) body.backdrop = d.backdrop;
  if (d.background !== undefined) body.background = d.background;
  if (d.layout !== undefined) body.layout = d.layout;
  if (d.cardOpacity !== undefined) body.cardOpacity = d.cardOpacity;
  if (d.logoSize !== undefined) body.logoSize = d.logoSize;
  return callGateway<SavedTheme>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/${enc(name)}`, { method: "PUT", body });
}

export const publishTheme = (i: CallerIdentity, ns: string, name: string) =>
  callGateway<{ published: string }>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/${enc(name)}/publish`, { method: "PUT" });

/**
 * Deletes a saved theme and its logo. radhotspotapi refuses the published theme
 * (409): publish another first. Needs the delete permission on portal themes
 * (`radhotspot:delete` at the gateway).
 */
export const deleteTheme = (i: CallerIdentity, ns: string, name: string) =>
  callGateway<{ deleted: string }>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/${enc(name)}`, { method: "DELETE" });

export const savedThemeLogo = (i: CallerIdentity, ns: string, name: string) =>
  callGatewayBytes(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/${enc(name)}/logo`);

export const effectiveThemeLogo = (i: CallerIdentity, ns: string) =>
  callGatewayBytes(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/logo`);

export const savedThemeBackground = (i: CallerIdentity, ns: string, name: string) =>
  callGatewayBytes(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/${enc(name)}/background`);

export const effectiveThemeBackground = (i: CallerIdentity, ns: string) =>
  callGatewayBytes(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/background`);
